import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import TableCulturalModule from "src/views/tables/TableCulturalModule";
import DialogUserStatusUpdate from "src/views/dialog/DialogUserStatusUpdate";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import { useAuth } from "src/hooks/useAuth";
import { canCreate, canUpdate, canRemove } from "src/utils/permissions";

const CulturalModulePage = () => {
  const { givenPermission, user } = useAuth();
  const navigate = useNavigate();
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [culturalModules, setCulturalModules] = useState([]);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const [culturalsModules, setCulturalsModules] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedCulturalModule, setSelectedCulturalModule] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [culturalModuleToDelete, setCulturalModuleToDelete] = useState(null);

  // Check permissions for cultural module
  const canCreateCultural = canCreate(givenPermission, "/cultural-module", user?.user_type);
  const canUpdateCultural = canUpdate(givenPermission, "/cultural-module", user?.user_type);
  const canDeleteCultural = canRemove(givenPermission, "/cultural-module", user?.user_type);

  const toggleConfirm = (e, culturalModule = null) => {
    setConfirmOpen((prev) => !prev);
    setCulturalModuleToDelete(culturalModule);
  };

  // Function to open status dialog
  const handleStatusDialogOpen = (culturalModule) => {
    setSelectedCulturalModule(culturalModule);
    setStatusDialogOpen(true);
  };

  // Function to close status dialog
  const handleStatusDialogClose = () => {
    setSelectedCulturalModule(null);
    setStatusDialogOpen(false);
  };

  const handleAddCulturalModule = () => {
    navigate("/cultural-module/add");
  };

  const handleEditCulturalModule = (e, culturalModule) => {
    navigate(`/cultural-module/edit/${culturalModule.id}`);
  };

  const fetchData = ({ currentPage, pageSize, search }) => {
    setLoading(true);
    const params = { page: currentPage, limit: pageSize, search };

    axiosInstance
      .get(ApiEndPoints.CULTURAL.list, { params })
      .then((response) => {
        setCulturalsModules(response.data?.data?.modules || []);
        setTotalCount(response.data?.data?.totalRecords || 0);
      })
      .catch((error) => toastError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData({ currentPage, pageSize, search });
  }, [currentPage, pageSize, search]);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setCurrentPage(1);
    }, 500);
  };

  const onConfirmDelete = () => {
    axiosInstance
      .delete(ApiEndPoints.CULTURAL.delete(culturalModuleToDelete?.id))
      .then((response) => {
        toastSuccess(response.data?.message || "Cultural Module deleted successfully");
        fetchData({ currentPage, pageSize, search });
        setConfirmOpen(false);
        setCulturalModuleToDelete(null);
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <Box sx={{
            p: 5,
            mb: 0,
          }}>
            <PageHeader
              title={
                <Typography variant="h5">
                  <Translations text="Cultural Module" />
                </Typography>
              }
              action={
                canCreateCultural ? (
                  <Button
                    variant="contained"
                    onClick={handleAddCulturalModule}
                  >
                    Add Cultural Module
                  </Button>
                ) : null
              }
            />
          </Box>
          <Card>
            <Box
              sx={{
                p: 5,
                pb: 0,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Box
                sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end" }}
              >
                <TextField
                  type="search"
                  size="small"
                  placeholder="Search"
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
            <Box sx={{ p: 5 }}>
              <TableCulturalModule
                rows={culturalsModules}
                loading={loading}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleEdit={handleEditCulturalModule}
                toggleStatus={handleStatusDialogOpen}
                toggleDelete={toggleConfirm}
                canEdit={canUpdateCultural}
                canDelete={canDeleteCultural}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <DialogUserStatusUpdate
        open={statusDialogOpen}
        toggle={handleStatusDialogClose}
        dataToEdit={selectedCulturalModule}
        endpointType="CULTURAL"
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
      />

      <DialogConfirmation
        open={confirmOpen}
        toggle={toggleConfirm}
        title="Delete Cultural Module"
        subtitle="Are you sure you want to delete this cultural module?"
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default CulturalModulePage;
